/**
 * Per-conversation file memory — survives page reloads.
 *
 * Why this exists:
 *   The agent's session history (Anthropic message log) often loses the
 *   reference to a previously uploaded file — older tool_use blocks may be
 *   truncated, and the server-side /tmp/agent_upload_* path gets cleaned up
 *   on container restart. From the user's perspective they uploaded a file
 *   ONCE in a conversation; they expect "try again" / "just parse" to work
 *   without re-uploading — including after a page reload.
 *
 * Storage:
 *   - In-memory Map (fastest path) — populated on rememberFile() and lazily
 *     rehydrated from IndexedDB on first getRememberedFile() miss.
 *   - IndexedDB blob store — persists the actual file bytes across reloads.
 *   - localStorage marker — lightweight metadata (filename / size / mime /
 *     attached_at) the UI uses to render "Using last uploaded file" hint
 *     before any async work completes.
 */

const META_KEY = (conversationId: string) =>
  `axondesktop.lastFile.${conversationId}`;

/** Live File objects keyed by conversation id (in-memory hot cache). */
const FILE_CACHE = new Map<string, File>();

export interface FileMemoryMeta {
  filename: string;
  size_bytes: number;
  mime_type: string;
  attached_at: string;
}

// ── IndexedDB layer ──────────────────────────────────────────────────────────

const DB_NAME = 'axondesktop.fileMemory';
const DB_VERSION = 1;
const STORE = 'files';

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE); // keyed by conversationId
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function idbPut(conversationId: string, file: File): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(
        {
          name: file.name,
          type: file.type || 'application/octet-stream',
          lastModified: file.lastModified,
          blob: file, // browsers store File as Blob just fine
        },
        conversationId,
      );
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* IndexedDB unavailable / quota — fall back to in-memory only */
  }
}

async function idbGet(conversationId: string): Promise<File | null> {
  try {
    const db = await openDB();
    return await new Promise<File | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(conversationId);
      req.onsuccess = () => {
        const v = req.result as
          | { name: string; type: string; lastModified: number; blob: Blob }
          | undefined;
        if (!v) {
          resolve(null);
          return;
        }
        // Reconstruct a File from the stored Blob so MIME + name are preserved.
        const file = new File([v.blob], v.name, {
          type: v.type,
          lastModified: v.lastModified,
        });
        resolve(file);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbDelete(conversationId: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(conversationId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore */
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Remember a file for this conversation (in-memory + IndexedDB + meta). */
export function rememberFile(conversationId: string, file: File): void {
  if (!conversationId || !file) return;
  FILE_CACHE.set(conversationId, file);
  const meta: FileMemoryMeta = {
    filename: file.name,
    size_bytes: file.size,
    mime_type: file.type || 'application/octet-stream',
    attached_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(META_KEY(conversationId), JSON.stringify(meta));
  } catch {
    /* ignore */
  }
  // Fire-and-forget persistence to IndexedDB so reloads can rehydrate
  idbPut(conversationId, file).catch(() => {});
  window.dispatchEvent(
    new CustomEvent('axondesktop.file.remembered', {
      detail: { conversationId, meta },
    }),
  );
}

/**
 * Get the remembered File for a conversation.
 *
 * Returns the in-memory copy if available (fast path). If the page was just
 * reloaded and the in-memory cache is empty, falls back to IndexedDB and
 * rehydrates the cache for next time. Returns null only when nothing has ever
 * been remembered for this conversation.
 */
export async function getRememberedFile(
  conversationId: string,
): Promise<File | null> {
  if (!conversationId) return null;
  const hot = FILE_CACHE.get(conversationId);
  if (hot) return hot;
  const cold = await idbGet(conversationId);
  if (cold) FILE_CACHE.set(conversationId, cold);
  return cold;
}

/** Sync variant — returns hot cache only. Use for tight paths that can't await. */
export function getRememberedFileSync(conversationId: string): File | null {
  return FILE_CACHE.get(conversationId) ?? null;
}

/** Get metadata for the remembered file (works after reload, sync). */
export function getFileMeta(conversationId: string): FileMemoryMeta | null {
  if (!conversationId) return null;
  try {
    const raw = localStorage.getItem(META_KEY(conversationId));
    if (!raw) return null;
    return JSON.parse(raw) as FileMemoryMeta;
  } catch {
    return null;
  }
}

/** Forget — call on conversation delete or explicit "use different file". */
export function forgetFile(conversationId: string): void {
  FILE_CACHE.delete(conversationId);
  try {
    localStorage.removeItem(META_KEY(conversationId));
  } catch {
    /* ignore */
  }
  idbDelete(conversationId).catch(() => {});
  window.dispatchEvent(
    new CustomEvent('axondesktop.file.forgotten', {
      detail: { conversationId },
    }),
  );
}

/** Has the file object actually been loaded into the hot cache? */
export function hasLiveFile(conversationId: string): boolean {
  return FILE_CACHE.has(conversationId);
}
